import { Dialog } from '@angular/cdk/dialog';
import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink, RouterModule } from '@angular/router';
import {
  CreateProviderAppointmentResponse,
  NavigationItem,
  PaymentCollectionStrategy,
} from '@models';
import {
  ActiveBookingIntentService,
  ActiveProviderService,
  ActiveTelehealthCallService,
  AuthenticationService,
  BookingIntentCompletionService,
  BookingIntentsService,
  ChatUnreadService,
  CHECKOUT_SERVICE,
  MeService,
  NavigationService,
  PatientDocumentsService,
  ProviderAppointmentsNotificationService,
  SupportTicketsService,
  ToastService,
} from '@services';
import {
  BookAppointmentPatientDialogComponent,
  SupportTicketDialogComponent,
  ButtonComponent,
  FileInputComponent,
  SpinnerComponent,
  ConfirmDialogComponent,
  CheckoutDialogComponent,
  SetupPaymentMethodDialogComponent,
} from '@ui-lib';
import { catchError, EMPTY, finalize, of, switchMap, take, tap } from 'rxjs';

@Component({
  selector: 'patient-portal-sidebar-menu',
  imports: [
    NgClass,
    MatIconModule,
    RouterModule,
    RouterLink,
    ButtonComponent,
    FileInputComponent,
    SpinnerComponent,
  ],
  providers: [
    { provide: CHECKOUT_SERVICE, useExisting: BookingIntentsService },
  ],
  templateUrl: './patient-portal-sidebar-menu.component.html',
})
export class PatientPortalSidebarMenuComponent {
  @ViewChild(FileInputComponent, { static: true })
  fileInputComponent!: FileInputComponent;
  private readonly authenticationService = inject(AuthenticationService);
  private readonly navigationService = inject(NavigationService);
  private readonly patientDocumentsService = inject(PatientDocumentsService);
  private readonly meService = inject(MeService);
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly activeProviderService = inject(ActiveProviderService);
  private readonly bookingIntentsService = inject(BookingIntentsService);
  private readonly providerAppointmentsNotificationService = inject(
    ProviderAppointmentsNotificationService
  );
  private readonly toast = inject(ToastService);
  private readonly activeIntentService = inject(ActiveBookingIntentService);
  private readonly bookingIntentCompletionService = inject(
    BookingIntentCompletionService
  );
  private readonly injector = inject(Injector);

  private readonly telehealthCall = inject(ActiveTelehealthCallService);

  readonly me = this.meService.me;

  readonly totalUnreadCount = inject(ChatUnreadService).totalUnreadCount;

  private readonly onlineMeetingItem: NavigationItem = {
    label: 'Online Meeting',
    route: 'online-meeting',
    icon: 'videocamera',
    data: { live: true },
  };

  private readonly activeProvider = toSignal(
    this.activeProviderService.getActiveProviderData()
  );

  activeBookingIntent = toSignal(
    this.activeIntentService
      .getBookingIntentData()
      .pipe(tap(() => this.loadingBookingIntent.set(false)))
  );

  loadingBookingIntent = signal(true);
  loadingFile = signal(false);
  navItems = computed(() => {
    const items = this.navigationService.getItems();
    const withMeeting = this.telehealthCall.isOpen()
      ? [...items, this.onlineMeetingItem]
      : items;
    return withMeeting.map(item =>
      item.children && item.children.length > 0
        ? { ...item, collapsed: false }
        : item
    ) as (NavigationItem & { collapsed: boolean })[];
  });

  get fullName(): string | undefined {
    const current = this.me();
    return current ? `${current.givenName} ${current.familyName}` : undefined;
  }

  get email(): string | null | undefined {
    return this.me()?.email;
  }

  get isSidebarOpen(): boolean {
    return this.navigationService.isSidebarOpen();
  }

  get resumeBookingLabel(): string {
    const intent = this.activeBookingIntent();
    if (intent?.checkoutSessionStatus === 'Complete') {
      return 'Complete booking';
    }
    return intent?.paymentCollectionStrategy ===
      PaymentCollectionStrategy.SetupWithoutCharging
      ? 'Resume setup'
      : 'Resume payment';
  }

  get canAcceptPayments(): boolean {
    return this.activeProvider()?.canAcceptPayments !== false;
  }

  get shouldShowResumeBookingAction(): boolean {
    return !!this.activeBookingIntent() && this.canAcceptPayments;
  }

  private fileData = computed(() => this.fileInputComponent.fileData());
  private fileUploadedEffect = effect(() => {
    if (!this.fileData()) {
      return;
    }

    this.loadingFile.set(true);
    this.openUploadDocumentDisclaimer();
  });

  closeSidebar(): void {
    this.navigationService.closeSidebar();
  }

  logout(): void {
    this.authenticationService.logout(true);
    this.router.navigate(['/auth']);
  }

  openSupportTicketDialog(): void {
    this.dialog.open(SupportTicketDialogComponent, {
      providers: [{ provide: SupportTicketsService }],
    });
  }
  openFilePicker(): void {
    this.fileInputComponent.openFilePicker();
  }

  openUploadDocumentDisclaimer(): void {
    this.dialog
      .open<boolean>(ConfirmDialogComponent, {
        data: {
          title: 'Confirm uploading new document',
          message:
            'Once reviewed by your provider or after midnight of the upload day, uploaded records become part of your permanent medical record and cannot be deleted by you.',
          confirmButtonLabel: 'Confirm and upload',
          onConfirm: () => of(true),
        },
      })
      .closed.pipe(
        take(1),
        switchMap(accepted => {
          if (!accepted || !this.fileData()) {
            return EMPTY;
          }

          const formData = new FormData();
          formData.append('file', this.fileData()!);

          return this.patientDocumentsService.uploadDocument(formData).pipe(
            catchError((error: HttpErrorResponse) => {
              const fileErrors = error?.error?.errors?.File;

              if (fileErrors?.length) {
                this.toast.showWarning(fileErrors[0]);
                console.error('Error uploading document:', fileErrors[0]);
                return EMPTY;
              }

              console.error('Error uploading document:', error);
              this.toast.showWarning(
                'Failed to upload document. Please try again.'
              );
              return EMPTY;
            }),
            finalize(() =>
              this.toast.showSuccess('Document uploaded successfully.')
            )
          );
        }),
        finalize(() => this.loadingFile.set(false))
      )
      .subscribe();
  }

  async openNewAppointmentDialog(): Promise<void> {
    try {
      this.dialog
        .open<CreateProviderAppointmentResponse>(
          BookAppointmentPatientDialogComponent,
          { data: { activeProvider: this.activeProvider() } }
        )
        .closed.pipe(
          catchError(() => {
            this.toast.showWarning('Failed to book an appointment.');
            return EMPTY;
          })
        )
        .subscribe(result => {
          if (result) {
            this.providerAppointmentsNotificationService.emit(result);
            this.toast.showSuccess('Your appointment is booked.');
          }
        });
    } catch (error) {
      console.error(
        'Failed to load BookAppointmentPatientDialogComponent:',
        error
      );
    }
  }

  openCheckoutDialog() {
    if (this.activeProvider()?.canAcceptPayments === false) {
      return;
    }

    const bookingIntent = this.activeBookingIntent();
    if (!bookingIntent) {
      return;
    }

    if (bookingIntent.checkoutSessionStatus === 'Complete') {
      this.bookingIntentCompletionService
        .signConsentAndConfirm(bookingIntent.id)
        .subscribe(result => {
          if (result) {
            this.activeIntentService.reload();
            this.providerAppointmentsNotificationService.emit(result);
            this.toast.showSuccess('Your appointment is booked.');
          }
        });
      return;
    }

    const dialogData = {
      itemId: bookingIntent.id,
      servicesCost: bookingIntent.servicesCost,
      taxPercentage: bookingIntent.taxPercentage,
    };

    if (
      bookingIntent.paymentCollectionStrategy ===
      PaymentCollectionStrategy.SetupWithoutCharging
    ) {
      this.dialog
        .open(SetupPaymentMethodDialogComponent, {
          data: dialogData,
          injector: this.injector,
        })
        .closed.subscribe();

      return;
    }

    const providerId = this.activeProvider()?.providerId;
    if (!providerId) {
      return;
    }

    this.bookingIntentsService
      .createCheckoutSession({
        itemId: bookingIntent.id,
        providerId,
      })
      .subscribe({
        next: result => {
          this.dialog
            .open(CheckoutDialogComponent, {
              data: {
                servicesCost: bookingIntent.servicesCost,
                subtotalCents: result.subtotalCents,
                taxAmountCents: result.taxAmountCents,
                totalCents: result.totalCents,
                taxPercentage: result.taxPercentage,
                checkoutUrl: result.url,
                bookingIntentId: bookingIntent.id,
              },
              injector: this.injector,
            })
            .closed.subscribe();
        },
        error: err => {
          if (
            err?.status === 409 &&
            err.error.code === 'CHECKOUT_SESSION_IS_ALREADY_COMPLETED'
          ) {
            this.toast.showSuccess('Payment has already been completed!');
          }
        },
      });
  }
}
